/**
 * Custom hooks for Carriers API
 *
 * Provides hooks for listing carriers from the carriers table
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface CarrierItem {
  scac: string;
  name: string;
  carrier_type: string;
  country_code?: string;
  is_active: boolean;
}

export interface CarriersResponse {
  carriers: CarrierItem[];
  total: number;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const CARRIERS_QUERY_KEYS = {
  all: ["carriers"] as const,
  list: (filters?: { q?: string; carrier_type?: string; is_active?: boolean }) =>
    [...CARRIERS_QUERY_KEYS.all, "list", filters] as const,
};

// ============================================================================
// LIST CARRIERS HOOK
// ============================================================================

/**
 * Hook to list carriers from the carriers table
 *
 * @param filters - Optional filters (q for search, carrier_type, is_active)
 * @param enabled - Whether the query should be enabled
 *
 * Returns all active carriers by default, sorted alphabetically by name
 */
export function useCarriers(
  filters?: {
    q?: string;
    carrier_type?: string;
    is_active?: boolean;
  },
  enabled: boolean = true
) {
  return useQuery({
    queryKey: CARRIERS_QUERY_KEYS.list(filters),
    queryFn: async ({ signal }): Promise<CarriersResponse> => {
      const params: Record<string, string | number | boolean> = {
        limit: 500, // Get all carriers (not too many)
      };
      if (filters?.q) params.q = filters.q;
      if (filters?.carrier_type) params.carrier_type = filters.carrier_type;
      if (filters?.is_active !== undefined) params.is_active = filters.is_active;
      else params.is_active = true; // Default to active only

      return await apiGet<CarriersResponse>("/carriers", { params, signal });
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes - carriers don't change often
  });
}
